import { useAtomValue, useSetAtom } from 'jotai'
import { countryState } from '@/core/store/country.store'
import { validateReferrer } from '@/core/checkout/checkout.api'
import { checkoutReferralIdAtom, checkoutReferralInfoAtom } from '@/core/checkout/checkout.store'
import { fetchFeelGreatSettings } from '@/core/feelgreat/feelgreat.api'
import { feelgreatSettingsAtom } from '@/core/feelgreat/feelgreat.store'
import { selectedFlavorAtom, selectedPurchaseOptionAtom } from '@/core/products/products.store'
import { localize } from '@/core/utils'

/**
 * Validate a referral code and, when valid, apply it to checkout state
 * and reload FeelGreat settings for the referrer's baStatus.
 * Returns true when the code was accepted. Network errors propagate.
 */
export const useApplyReferral = () => {
    const country = useAtomValue(countryState)
    const setReferralInfo = useSetAtom(checkoutReferralInfoAtom)
    const setReferralId = useSetAtom(checkoutReferralIdAtom)
    const setFeelgreatSettings = useSetAtom(feelgreatSettingsAtom)
    const setSelectedFlavor = useSetAtom(selectedFlavorAtom)
    const setSelectedPurchaseOption = useSetAtom(selectedPurchaseOptionAtom)

    return async (code: string): Promise<boolean> => {
        const result = await validateReferrer(country.short, code)
        if (!result.isAllow) return false

        setReferralInfo({
            baId: result.baId,
            displayName: localize(result.displayName) || result.baId,
            baStatus: result.baStatus
        })
        setReferralId(code)

        const settingsData = await fetchFeelGreatSettings(country.country, result.baStatus)
        setFeelgreatSettings(settingsData)
        if (settingsData.defaultValues?.flavor) setSelectedFlavor(settingsData.defaultValues.flavor)
        if (settingsData.defaultValues?.purchaseOption) setSelectedPurchaseOption(settingsData.defaultValues.purchaseOption)
        return true
    }
}
